import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../../styles/settings/geofence.css";
import {
  Box,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Button,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LocationOffRoundedIcon from "@mui/icons-material/LocationOffRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import { useNavigate } from "react-router-dom";
import { GetUsers } from "../../hooks/useUserData";
import { useGeofence } from "../../hooks/useGeofence";
import { useAlarmLatestPositions } from "../../hooks/useAlarmLatestPositions";
import { isInsidePolygon, polygonBounds } from "../../utils/geofence";

const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

// Generous Sweden bbox — used to discard bogus coordinates (test data, 0/0,
// foreign GPS noise) that would otherwise drag fitBounds out to a continental
// view and hide the actual geofence.
const SWEDEN_BBOX = { minLat: 55.0, maxLat: 69.2, minLng: 10.5, maxLng: 24.5 };
const isInSweden = (lat, lng) =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= SWEDEN_BBOX.minLat &&
  lat <= SWEDEN_BBOX.maxLat &&
  lng >= SWEDEN_BBOX.minLng &&
  lng <= SWEDEN_BBOX.maxLng;

const pinIcon = (kind, label) =>
  L.divIcon({
    className: "alarm-pin-wrapper",
    html: `<div class="alarm-pin ${kind}">${label}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

const initials = (user) => {
  const f = (user?.first_name || "").trim();
  const l = (user?.last_name || "").trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "?";
  return "?";
};

const displayName = (user) =>
  `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() ||
  user?.email ||
  `Användare ${user?.id}`;

export default function LatestAlarmMap({ alarm, mapHeight = 540, sideWidth = 340 }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const polygonLayerRef = useRef(null);
  const markersLayerRef = useRef(null);

  const [organisationId] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem("userData") || "{}").organisation_id ??
        null
      );
    } catch {
      return null;
    }
  });

  const { data: geofence, isLoading: loadingFence } =
    useGeofence(organisationId);
  const { data: usersResp, isLoading: loadingUsers } = GetUsers();

  const orgUsers = useMemo(() => {
    const list = usersResp?.data ?? [];
    const alarmGroupId = alarm?.group_id ?? null;
    return list.filter((u) => {
      if (organisationId != null && u.organisation_id != null) {
        if (String(u.organisation_id) !== String(organisationId)) return false;
      }
      if (alarmGroupId != null) {
        if (u.group_id == null) return false;
        return String(u.group_id) === String(alarmGroupId);
      }
      return true;
    });
  }, [usersResp, organisationId, alarm?.group_id]);

  const { byUserId: positionByUserId, isLoading: loadingPositions } =
    useAlarmLatestPositions(alarm?.id);

  const evaluated = useMemo(() => {
    const poly = geofence?.polygon ?? null;
    return orgUsers.map((user) => {
      const point = positionByUserId.get(user.id) ?? null;
      if (!point || !isInSweden(point.latitude, point.longitude)) {
        return {
          userId: user.id,
          user,
          point: null,
          inside: null,
          status: "missing",
        };
      }
      const inside = poly
        ? isInsidePolygon({ lat: point.latitude, lng: point.longitude }, poly)
        : null;
      return { userId: user.id, user, point, inside, status: "success" };
    });
  }, [orgUsers, positionByUserId, geofence]);

  const counts = useMemo(() => {
    let inside = 0;
    let outside = 0;
    let unknown = 0;
    for (const e of evaluated) {
      if (e.inside === true) inside++;
      else if (e.inside === false) outside++;
      else unknown++;
    }
    return { inside, outside, unknown, total: evaluated.length };
  }, [evaluated]);

  // Map init
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      preferCanvas: true,
      zoomSnap: 0.25,
      zoomDelta: 0.5,
      // Centered on south/central Sweden — fixed zoom so the framing is
      // independent of container width (fitBounds gets dragged out by
      // Sweden's vertical span on wide containers).
      center: [60.5, 16.5],
      zoom: 5.25,
    });
    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTRIBUTION,
      subdomains: "abcd",
      maxZoom: 19,
      detectRetina: true,
    }).addTo(map);
    polygonLayerRef.current = L.layerGroup().addTo(map);
    markersLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    const resize = () => map.invalidateSize();
    window.addEventListener("resize", resize);
    const t = setTimeout(resize, 60);

    // Watch the actual container — fixes the case where the parent grid
    // breakpoint flips (side panel appears/disappears) without a window
    // resize event, leaving the canvas at its stale dimensions.
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => map.invalidateSize())
        : null;
    if (ro && containerRef.current) ro.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", resize);
      clearTimeout(t);
      if (ro) ro.disconnect();
      map.remove();
      mapRef.current = null;
      polygonLayerRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);

  // Re-invalidate when the configured size changes — useful when the
  // details view passes a custom mapHeight/sideWidth that shifts after
  // first paint.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const timers = [10, 80, 250, 600].map((ms) =>
      setTimeout(() => map.invalidateSize(), ms),
    );
    return () => timers.forEach(clearTimeout);
  }, [mapHeight, sideWidth]);

  // Draw polygon
  useEffect(() => {
    const layer = polygonLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const poly = geofence?.polygon;
    if (!poly?.coordinates?.[0]) return;

    const latlngs = poly.coordinates[0].map(([lng, lat]) => [lat, lng]);

    // Outer halo: wider, semi-transparent stroke that stays visible when
    // the map is zoomed out far enough that the polygon itself becomes a dot.
    L.polyline(latlngs.concat([latlngs[0]]), {
      color: "#8a1125",
      weight: 8,
      opacity: 0.18,
      lineCap: "round",
      lineJoin: "round",
      interactive: false,
    }).addTo(layer);

    L.polygon(latlngs, {
      color: "#8a1125",
      weight: 3,
      opacity: 0.95,
      fillOpacity: 0.22,
    }).addTo(layer);

    // Centroid pin — guarantees the area is locatable at any zoom level,
    // even when the polygon collapses to a single pixel.
    const centroid = latlngs.reduce(
      (acc, [lat, lng]) => [acc[0] + lat, acc[1] + lng],
      [0, 0],
    );
    centroid[0] /= latlngs.length;
    centroid[1] /= latlngs.length;
    L.circleMarker(centroid, {
      radius: 6,
      color: "#ffffff",
      weight: 2,
      fillColor: "#8a1125",
      fillOpacity: 1,
      interactive: false,
    }).addTo(layer);
  }, [geofence]);

  // Draw markers + fit bounds
  useEffect(() => {
    const map = mapRef.current;
    const markers = markersLayerRef.current;
    if (!map || !markers) return;

    markers.clearLayers();

    const points = [];
    evaluated.forEach((e) => {
      if (!e.point) return;
      const lat = e.point.latitude;
      const lng = e.point.longitude;
      points.push([lat, lng]);
      const kind =
        e.inside === true
          ? "inside"
          : e.inside === false
            ? "outside"
            : "unknown";
      const name = displayName(e.user);
      const when = e.point.recorded_at
        ? new Date(e.point.recorded_at).toLocaleTimeString("sv-SE")
        : "";
      const marker = L.marker([lat, lng], {
        icon: pinIcon(kind, initials(e.user)),
      });
      marker.bindPopup(
        `<div style="font-family:'DM Sans',sans-serif;min-width:160px">` +
          `<div style="font-weight:600;color:#10324a">${name}</div>` +
          `<div style="color:#5f6877;font-size:12px">${
            kind === "inside"
              ? "På plats"
              : kind === "outside"
                ? "Ej på plats"
                : "Okänd position"
          }</div>` +
          (when
            ? `<div style="color:#9aa1ab;font-size:11px">kl ${when}</div>`
            : "") +
          `</div>`,
      );
      marker.addTo(markers);
    });

    // Fit bounds to polygon + points.
    const bounds = L.latLngBounds([]);
    const pb = polygonBounds(geofence?.polygon);
    if (pb) {
      bounds.extend(pb[0]);
      bounds.extend(pb[1]);
    }
    points.forEach((p) => bounds.extend(p));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
    }
  }, [evaluated, geofence]);

  const isLoading = loadingFence || loadingUsers || loadingPositions;

  if (!alarm) {
    return (
      <Box
        sx={{
          height: Math.max(450, typeof mapHeight === "number" ? mapHeight - 60 : 450),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <LocationOffRoundedIcon sx={{ fontSize: 40, color: "#9aa1ab" }} />
        <Typography variant="body1" sx={{ color: "#5f6877" }}>
          Inga registrerade larm att visa.
        </Typography>
      </Box>
    );
  }

  if (!loadingFence && !geofence?.polygon?.coordinates?.[0]) {
    return (
      <Box
        sx={{
          height: Math.max(450, typeof mapHeight === "number" ? mapHeight - 60 : 450),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <WarningAmberRoundedIcon sx={{ fontSize: 44, color: "#8a1125" }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a1d23" }}>
          Inget platsområde konfigurerat
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#5f6877", textAlign: "center", maxWidth: 360 }}
        >
          För att kunna visa vilka användare som var på plats när ett larm
          utlöstes behöver ni först rita upp ert område i inställningarna.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/admin/settings/geofence")}
          sx={{ mt: 1 }}
        >
          Konfigurera platsområde
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberRoundedIcon sx={{ color: "#8a1125" }} />
            <Typography
              variant="subtitle2"
              sx={{
                color: "#5f6877",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 600,
              }}
            >
              Senaste larm
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: "#9aa1ab", mt: 0.5 }}>
            {alarm?.started
              ? new Date(alarm.started).toLocaleString("sv-SE")
              : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            icon={<CheckCircleRoundedIcon style={{ color: "#ffffff" }} />}
            label={`${counts.inside} av ${counts.total} på plats`}
            sx={{
              background: "linear-gradient(135deg,#1b7a4a,#1fb07a)",
              color: "white",
              fontWeight: 600,
              px: 0.5,
            }}
          />
          <Chip
            icon={<CancelRoundedIcon style={{ color: "#ffffff" }} />}
            label={`${counts.outside} av ${counts.total} ej på plats`}
            sx={{
              background: "linear-gradient(135deg,#10324a,#34567a)",
              color: "white",
              fontWeight: 600,
              px: 0.5,
            }}
          />
          {counts.unknown > 0 && (
            <Chip
              icon={<HelpRoundedIcon style={{ color: "#10324a" }} />}
              label={`${counts.unknown} okänd`}
              sx={{ background: "#eef2f6", color: "#10324a", fontWeight: 600 }}
            />
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: `minmax(0, 1fr) ${sideWidth}px` },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: mapHeight,
            borderRadius: 3,
            overflow: "hidden",
            background: "#eef2f6",
            boxShadow: "inset 0 0 0 1px rgba(16,50,74,0.05)",
          }}
        >
          <div
            ref={containerRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100%",
            }}
          />
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.6)",
                backdropFilter: "blur(2px)",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>

        <PresenceList
          evaluated={evaluated}
          isLoading={isLoading}
          mapHeight={mapHeight}
        />
      </Box>
    </Box>
  );
}

const formatCoord = (n) =>
  typeof n === "number" && Number.isFinite(n) ? n.toFixed(5) : "—";

const formatRecorded = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  return sameDay
    ? `kl ${date.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}`
    : date.toLocaleString("sv-SE", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

function PresenceRow({ entry, kind }) {
  const accent =
    kind === "inside"
      ? { dot: "#1b7a4a" }
      : kind === "outside"
        ? { dot: "#5f6877" }
        : { dot: "#b8bcc4" };

  const when = formatRecorded(entry.point?.recorded_at);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.75,
        py: 1,
        borderRadius: 2,
        transition: "background 0.18s ease",
        "&:hover": { background: "rgba(16,50,74,0.04)" },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "50%",
          background: accent.dot,
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'DM Sans', system-ui, sans-serif",
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: "0.02em",
          border: "2px solid #ffffff",
        }}
      >
        {initials(entry.user)}
      </Box>
      <Typography
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          display: "block",
          margin: 0,
          fontFamily: "'DM Sans', system-ui, sans-serif",
          fontWeight: 600,
          fontSize: 13.5,
          color: "#10324a",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={displayName(entry.user)}
      >
        {displayName(entry.user)}
      </Typography>
      <Typography
        component="span"
        sx={{
          flexShrink: 0,
          margin: 0,
          fontFamily: "'Outfit', 'DM Sans', sans-serif",
          fontSize: 11.5,
          color: "#9aa1ab",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "0.01em",
        }}
      >
        {when ?? "—"}
      </Typography>
    </Box>
  );
}

function PresenceSection({ kind, title, entries }) {
  const palette =
    kind === "inside"
      ? {
          eyebrow: "#1b7a4a",
          underline: "linear-gradient(90deg,#1b7a4a,#1fb07a 60%,transparent)",
          countBg: "linear-gradient(135deg,#1b7a4a,#1fb07a)",
        }
      : kind === "outside"
        ? {
            eyebrow: "#10324a",
            underline: "linear-gradient(90deg,#10324a,#34567a 60%,transparent)",
            countBg: "linear-gradient(135deg,#10324a,#34567a)",
          }
        : {
            eyebrow: "#7a8492",
            underline: "linear-gradient(90deg,#b8bcc4,transparent)",
            countBg: "#eef2f6",
          };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.75,
          pt: 1.5,
          pb: 1,
          position: "sticky",
          top: 0,
          background: "linear-gradient(180deg,#ffffff 70%,rgba(255,255,255,0))",
          zIndex: 1,
        }}
      >
        <Typography
          component="span"
          sx={{
            margin: 0,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: palette.eyebrow,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            minWidth: 28,
            height: 22,
            px: 1,
            borderRadius: 999,
            background: palette.countBg,
            color: kind === "unknown" ? "#10324a" : "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {entries.length}
        </Box>
      </Box>

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 0.25, pb: 0.5 }}
      >
        {entries.length === 0 ? (
          <Typography
            sx={{
              px: 1.75,
              py: 1.25,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12.5,
              color: "#9aa1ab",
              fontStyle: "italic",
            }}
          >
            {kind === "inside"
              ? "Ingen var registrerad på plats."
              : kind === "outside"
                ? "Alla med registrerad position var på plats."
                : "Inga okända positioner."}
          </Typography>
        ) : (
          entries.map((e) => (
            <PresenceRow key={e.userId} entry={e} kind={kind} />
          ))
        )}
      </Box>
    </Box>
  );
}

function PresenceList({ evaluated, isLoading, mapHeight = 540 }) {
  const inside = evaluated.filter((e) => e.inside === true);
  const outside = evaluated.filter((e) => e.inside === false);
  const unknown = evaluated.filter(
    (e) => e.inside !== true && e.inside !== false,
  );

  return (
    <Box
      sx={{
        position: "relative",
        height: { xs: "auto", xl: mapHeight },
        maxHeight: { xs: 520, xl: "none" },
        borderRadius: 3,
        background: "#ffffff",
        border: "1px solid rgba(16,50,74,0.06)",
        boxShadow:
          "0 1px 2px rgba(16,50,74,0.04), 0 24px 48px -28px rgba(16,50,74,0.18)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          height: 3,
          background:
            "linear-gradient(90deg,#1b7a4a 0%,#1fb07a 35%,#10324a 100%)",
        }}
      />
      <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
        <Typography
          sx={{
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 600,
            fontSize: 17,
            color: "#10324a",
            letterSpacing: "-0.005em",
            lineHeight: 1.2,
          }}
        >
          Närvaro vid larm
        </Typography>
        <Typography
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12,
            color: "#7a8492",
            mt: 0.25,
          }}
        >
          Senste GPS-position vid utlöst tidpunkt.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          mt: 1,
          px: 1,
          pb: 1,
          // Custom scrollbar for refinement.
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(16,50,74,0.15)",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(16,50,74,0.28)",
          },
        }}
      >
        <PresenceSection kind="inside" title="På plats" entries={inside} />
        <PresenceSection kind="outside" title="Ej på plats" entries={outside} />
        {unknown.length > 0 && (
          <>
            <PresenceSection
              kind="unknown"
              title="Okänd position"
              entries={unknown}
            />
          </>
        )}
      </Box>

      {isLoading && evaluated.length === 0 && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(2px)",
          }}
        >
          <CircularProgress size={22} />
        </Box>
      )}
    </Box>
  );
}
